import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import SuperAdminSidebar from "../../components/sidebars/SuperAdminSidebar";
import { superAdminAPI } from "../../lib/api";
import { getPlayerManagementPollIntervalMs } from "../../lib/utils";
import toast from "react-hot-toast";
import UnifiedPlayerManagement from "./UnifiedPlayerManagement";
import TableManagement from "./TableManagement";
import ClubBuyInCashOut from "./ClubBuyInCashOut";
import CreditManagement from "./CreditManagement";
import VIPStore from "./VIPStore";
import PushNotifications from "./PushNotifications";
import TournamentManagement from "../../components/TournamentManagement";
import StaffManagement from "../../components/StaffManagement";
import AttendanceManagement from "../../components/AttendanceManagement";
import PayrollManagement from "../../components/PayrollManagement";
import BonusManagement from "../../components/BonusManagement";
import AffiliateManagement from "../../components/AffiliateManagement";
import FinancialOverrides from "../../components/FinancialOverrides";
import FNBManagement from "../../components/FNBManagement";
import ChatManagement from "../../components/ChatManagement";
import ReportsAnalytics from "../../components/ReportsAnalytics";
import AuditLogsBackups from "../../components/AuditLogsBackups";
import RummyManagement from "../../components/RummyManagement";
import NotificationsInbox from "../../components/NotificationsInbox";
import SystemControl from "../../components/SystemControl";


export default function SuperAdminPortal() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeItem, setActiveItem] = useState("Dashboard");
  const [selectedClubId, setSelectedClubId] = useState(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Check authentication - redirect to sign-in if not logged in
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const superAdminUser = JSON.parse(localStorage.getItem('superadminuser') || '{}');

    // Check if user is logged in (either from 'user' or 'superadminuser')
    if (!user.id && !superAdminUser.userId) {
      navigate('/super-admin/signin');
      return;
    }

    // Check if user needs to reset password (check both storage keys)
    if (user.mustResetPassword || superAdminUser.mustResetPassword) {
      setShowPasswordResetModal(true);
    }
  }, [navigate]);

  // Load all clubs for this super admin — skip entirely if password reset is pending
  // (would return 401 and show a false "Error loading clubs" screen)
  const { data: clubs = [], isLoading: clubsLoading, error: clubsError } = useQuery({
    queryKey: ['superAdminClubs'],
    queryFn: superAdminAPI.getClubs,
    enabled: !showPasswordResetModal,
  });

  // Auto-select first club if none selected and clubs are loaded
  useEffect(() => {
    if (clubs && clubs.length > 0 && !selectedClubId) {
      setSelectedClubId(clubs[0].clubId);
    }
  }, [clubs, selectedClubId]);

  // Super Admin login does not set clubId in localStorage; keep it aligned with the selected club so APIs that still read headers default work.
  useEffect(() => {
    if (selectedClubId) {
      localStorage.setItem("clubId", selectedClubId);
    }
  }, [selectedClubId]);

  // Load revenue data for selected club
  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['clubRevenue', selectedClubId],
    queryFn: () => superAdminAPI.getClubRevenue(selectedClubId),
    enabled: !!selectedClubId,
  });

  // Load players for selected club
  const { data: playersData, isLoading: playersLoading } = useQuery({
    queryKey: ['clubPlayers', selectedClubId],
    queryFn: () => superAdminAPI.getAllPlayers(selectedClubId, { limit: 100 }),
    enabled: !!selectedClubId,
  });

  // Load pending approval players
  const { data: pendingPlayers = [], isLoading: pendingLoading } = useQuery({
    queryKey: ['pendingPlayers', selectedClubId],
    queryFn: () => superAdminAPI.getPendingApprovalPlayers(selectedClubId),
    enabled: !!selectedClubId,
    refetchInterval: getPlayerManagementPollIntervalMs(),
  });

  // Load suspended players
  const { data: suspendedPlayers = [], isLoading: suspendedLoading } = useQuery({
    queryKey: ['suspendedPlayers', selectedClubId],
    queryFn: () => superAdminAPI.getSuspendedPlayers(selectedClubId),
    enabled: !!selectedClubId,
  });

  const selectedClub = clubs.find((c) => c.clubId === selectedClubId);

  // Check if selected club is suspended or killed
  const isClubSuspended = selectedClub?.status === 'suspended';
  const isClubKilled = selectedClub?.status === 'killed';

  // Handle sign out
  const handleSignOut = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/super-admin/signin');
  };

  // Format currency
  const formatCurrency = (value) => `₹${(value || 0).toLocaleString("en-IN")}`;

  // Password reset mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async (data) => {
      // Call auth API to reset password
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3333/api'}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to reset password');
      }
      return response.json();
    },
    onSuccess: (data) => {
      // Backend now returns a fresh JWT after password reset — store it so the
      // user stays logged in without needing to log out and back in.
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('token', data.token);
      }
      // Clear mustResetPassword flag in stored user objects
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.mustResetPassword = false;
      localStorage.setItem('user', JSON.stringify(user));
      const superAdminUser = JSON.parse(localStorage.getItem('super_adminuser') || '{}');
      superAdminUser.mustResetPassword = false;
      localStorage.setItem('super_adminuser', JSON.stringify(superAdminUser));

      toast.success('Password reset successfully!');
      setShowPasswordResetModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      // Reload so all queries re-run with the fresh token
      window.location.reload();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reset password');
    },
  });

  const handlePasswordReset = (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const superAdminUser = JSON.parse(localStorage.getItem('superadminuser') || '{}');
    const email = user.email || superAdminUser.email;

    if (!email) {
      toast.error('User email not found. Please login again.');
      return;
    }
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('All fields are required');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    resetPasswordMutation.mutate({
      email: email,
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  // PASSWORD RESET MODAL - Render this FIRST before any other UI
  const passwordResetModal = showPasswordResetModal && (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[200]">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-8 max-w-md w-full border border-emerald-600 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="text-center mb-6">
          <div className="text-yellow-400 text-5xl mb-3">🔒</div>
          <h2 className="text-2xl font-bold text-white">Password Reset Required</h2>
          <p className="text-gray-400 mt-2">Please set a new password to continue</p>
        </div>

        <form onSubmit={handlePasswordReset} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Enter temporary password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              New Password
            </label>
            <input
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Enter new password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Confirm new password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={resetPasswordMutation.isLoading}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50"
          >
            {resetPasswordMutation.isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );

  // Render loading state - NO SIDEBAR
  if (clubsLoading) {
    return (
      <>
        {passwordResetModal}
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
            <p className="text-xl">Loading clubs...</p>
          </div>
        </div>
      </>
    );
  }

  // Show password reset modal over a blank screen — never show error screen when reset is pending
  if (showPasswordResetModal) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900">
        {passwordResetModal}
      </div>
    );
  }

  // Render error state - NO SIDEBAR
  if (clubsError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-8">
        <div className="bg-slate-800 rounded-xl p-12 text-center border border-red-600 max-w-2xl w-full">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <p className="text-xl text-red-400 mb-2">Error loading clubs</p>
          <p className="text-gray-400 mb-8">{clubsError.message}</p>
          <button
            onClick={handleSignOut}
            className="bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Render "No Clubs" state - NO SIDEBAR, just message and sign out
  if (!clubs || clubs.length === 0) {
    return (
      <>
        {passwordResetModal}
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-8">
          <div className="bg-slate-800 rounded-xl p-12 text-center border border-slate-700 max-w-2xl w-full">
            <div className="text-yellow-400 text-6xl mb-6">🏢</div>
            <h2 className="text-3xl font-bold text-white mb-4">No Clubs Created Yet</h2>
            <p className="text-gray-400 text-lg mb-8">
              You don't have any clubs assigned to you. Please contact the Master Admin to create a club for your tenant.
            </p>
            <div className="bg-slate-700 rounded-lg p-6 border border-slate-600 mb-8">
              <p className="text-sm text-gray-300">
                📞 Need help? Contact Master Admin to set up your first club and start managing players, tables, and tournaments.
              </p>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full max-w-xs mx-auto bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-lg"
            >
              Sign Out
            </button>
          </div>
        </div>
      </>
    );
  }

  // Render club suspended/killed screen for Super Admin
  if (isClubKilled) {
    return (
      <>
        {passwordResetModal}
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 flex items-center justify-center p-8">
          <div className="bg-slate-800 rounded-xl p-12 text-center border border-red-600 max-w-2xl w-full">
            <div className="text-red-400 text-6xl mb-4">🚫</div>
            <h1 className="text-3xl font-bold text-white mb-4">Club Not Found</h1>
            <p className="text-gray-400 mb-8">This club has been removed by Master Admin.</p>
            <button
              onClick={handleSignOut}
              className="bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </>
    );
  }

  if (isClubSuspended) {
    return (
      <>
        {passwordResetModal}
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 flex items-center justify-center p-8">
          <div className="bg-slate-800 rounded-xl p-12 text-center border border-orange-600 max-w-2xl w-full">
            <div className="text-orange-400 text-6xl mb-4">🔒</div>
            <h1 className="text-3xl font-bold text-white mb-4">Club Suspended</h1>
            <p className="text-gray-400 mb-4">This club has been temporarily suspended by Master Admin.</p>
            {selectedClub?.subscriptionNotes && (
              <div className="bg-orange-900/30 border border-orange-700 rounded-lg p-4 mb-8">
                <p className="text-sm text-orange-300 font-semibold mb-2">Suspension Reason:</p>
                <p className="text-gray-300">{selectedClub.subscriptionNotes}</p>
              </div>
            )}
            <p className="text-sm text-gray-500 mb-8">Please contact Master Admin for more information.</p>
            <button
              onClick={handleSignOut}
              className="bg-orange-600 hover:bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Password Reset Modal */}
      {passwordResetModal}

      <div className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900">
        <SuperAdminSidebar
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          clubs={clubs}
          selectedClubId={selectedClubId}
          onClubChange={setSelectedClubId}
          onSignOut={handleSignOut}
        />

        <main className="flex-1 p-8 overflow-y-auto">
          {/* Dashboard */}
          {activeItem === "Dashboard" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-white">
                  Dashboard - {selectedClub?.clubName || 'Loading...'}
                </h1>
              </div>

              {/* Revenue Cards */}
              {revenueLoading ? (
                <div className="text-white text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                  <p>Loading revenue data...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Previous Day Revenue */}
                  <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-gray-400 text-sm mb-2">Previous Day Revenue</h3>
                    <p className="text-3xl font-bold text-white">
                      {formatCurrency(revenueData?.previousDay?.revenue || 0)}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">{revenueData?.previousDay?.date || '-'}</p>
                  </div>

                  {/* Today's Revenue */}
                  <div className="bg-slate-800 rounded-xl p-6 border border-emerald-600">
                    <h3 className="text-gray-400 text-sm mb-2">Today's Revenue</h3>
                    <p className="text-3xl font-bold text-emerald-400">
                      {formatCurrency(revenueData?.currentDay?.revenue || 0)}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">{revenueData?.currentDay?.date || '-'}</p>
                  </div>

                  {/* Total Rake */}
                  <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-gray-400 text-sm mb-2">Today's Rake</h3>
                    <p className="text-3xl font-bold text-white">
                      {formatCurrency(revenueData?.currentDay?.rake || 0)}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">10% of revenue</p>
                  </div>

                  {/* Previous Day Rake */}
                  <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-gray-400 text-sm mb-2">Previous Day Rake</h3>
                    <p className="text-3xl font-bold text-white">
                      {formatCurrency(revenueData?.previousDay?.rake || 0)}
                    </p>
                  </div>

                  {/* Previous Day Tips */}
                  <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-gray-400 text-sm mb-2">Previous Day Tips</h3>
                    <p className="text-3xl font-bold text-white">
                      {formatCurrency(revenueData?.previousDay?.tips || 0)}
                    </p>
                  </div>

                  {/* Today's Tips */}
                  <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-gray-400 text-sm mb-2">Today's Tips</h3>
                    <p className="text-3xl font-bold text-white">
                      {formatCurrency(revenueData?.currentDay?.tips || 0)}
                    </p>
                  </div>
                </div>
              )}

              {/* Player & Staff Counts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-white mb-4">Players</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Players</span>
                      <span className="text-white font-semibold">
                        {playersLoading ? '...' : playersData?.players?.length || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Pending Approval</span>
                      <span className="text-yellow-400 font-semibold">
                        {pendingLoading ? '...' : pendingPlayers.length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Suspended</span>
                      <span className="text-red-400 font-semibold">
                        {suspendedLoading ? '...' : suspendedPlayers.length}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-white mb-4">Quick Actions</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => setActiveItem("Player Management")}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-2 rounded-lg transition-colors"
                    >
                      Manage Players
                    </button>
                    <button
                      onClick={() => setActiveItem("Player Approval")}
                      className="w-full bg-blue-600 hover:bg-blue-500 text-white py-2 rounded-lg transition-colors"
                    >
                      Review Approvals ({pendingLoading ? '...' : pendingPlayers.length})
                    </button>
                    <button
                      onClick={() => setActiveItem("Player Suspension")}
                      className="w-full bg-orange-600 hover:bg-orange-500 text-white py-2 rounded-lg transition-colors"
                    >
                      Suspended Players ({suspendedLoading ? '...' : suspendedPlayers.length})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Player Management - Unified with Tabs */}
          {activeItem === "Player Management" && (
            <UnifiedPlayerManagement
              selectedClubId={selectedClubId}
              playersData={playersData}
              playersLoading={playersLoading}
              pendingPlayers={pendingPlayers}
              pendingLoading={pendingLoading}
              suspendedPlayers={suspendedPlayers}
              suspendedLoading={suspendedLoading}
              allowPermanentDelete={true}
              onRefresh={() => {
                queryClient.invalidateQueries(['pendingPlayers', selectedClubId]);
                queryClient.invalidateQueries(['clubPlayers', selectedClubId]);
                queryClient.invalidateQueries(['suspendedPlayers', selectedClubId]);
              }}
            />
          )}

          {/* Tables & Waitlist Management */}
          {activeItem === "Tables & Waitlist" && (
            <TableManagement selectedClubId={selectedClubId} />
          )}

          {/* Club Buy-In & Cash Out */}
          {activeItem === "Club Buy-In" && (
            <ClubBuyInCashOut selectedClubId={selectedClubId} />
          )}

          {/* Credit Management */}
          {activeItem === "Credit Management" && (
            <CreditManagement selectedClubId={selectedClubId} />
          )}

          {/* VIP Store */}
          {activeItem === "VIP Store" && (
            <VIPStore selectedClubId={selectedClubId} />
          )}

          {/* Push Notifications */}
          {activeItem === "Push Notifications" && (
            <PushNotifications selectedClubId={selectedClubId} />
          )}

          {/* Tournaments */}
          {activeItem === "Tournaments" && (
            <TournamentManagement selectedClubId={selectedClubId} />
          )}

          {/* Staff Management */}
          {activeItem === "Staff Management" && (
            <StaffManagement selectedClubId={selectedClubId} />
          )}

          {activeItem === "Attendance Management" && selectedClubId && (
            <AttendanceManagement selectedClubId={selectedClubId} />
          )}

          {/* Payroll Management */}
          {activeItem === "Payroll Management" && (
            <PayrollManagement selectedClubId={selectedClubId} />
          )}

          {/* Bonus Management */}
          {activeItem === "Bonus Management" && (
            <BonusManagement selectedClubId={selectedClubId} />
          )}

          {/* Affiliate Management */}
          {activeItem === "Affiliates" && (
            <AffiliateManagement selectedClubId={selectedClubId} />
          )}

          {/* Transactions */}
          {activeItem === "Transactions" && (
            <FinancialOverrides selectedClubId={selectedClubId} />
          )}

          {/* Other sections placeholder */}
          {/* FNB Management */}
          {activeItem === "FNB" && selectedClubId && (
            <FNBManagement clubId={selectedClubId} />
          )}

          {activeItem === "Chat" && selectedClubId && (
            <ChatManagement clubId={selectedClubId} />
          )}

          {activeItem === "Reports & Analytics" && selectedClubId && (
            <ReportsAnalytics clubId={selectedClubId} allowCustomReport />
          )}

          {activeItem === "Audit Logs" && selectedClubId && (
            <AuditLogsBackups clubId={selectedClubId} />
          )}

          {/* Rummy Management */}
          {activeItem === "Rummy" && selectedClubId && (
            <RummyManagement selectedClubId={selectedClubId} />
          )}

          {/* Notifications Inbox */}
          {activeItem === "Notifications" && selectedClubId && (
            <NotificationsInbox selectedClubId={selectedClubId} recipientType="staff" />
          )}

          {/* System Control */}
          {activeItem === "System Control" && selectedClubId && (
            <SystemControl clubId={selectedClubId} />
          )}

          {/* Player Feedback */}
          {activeItem === "Player Feedback" && selectedClubId && (
            <PlayerFeedbackSection selectedClubId={selectedClubId} />
          )}

          {!["Dashboard", "Player Management", "Tables & Waitlist", "Club Buy-In", "Credit Management", "VIP Store", "Push Notifications", "Tournaments", "Staff Management", "Attendance Management", "Payroll Management", "Bonus Management", "Affiliates", "Transactions", "Player Feedback", "FNB", "Chat", "Reports & Analytics", "Audit Logs", "Rummy", "System Control", "Notifications"].includes(activeItem) && (
            <div className="text-white">
              <h1 className="text-3xl font-bold mb-6">{activeItem}</h1>
            </div>
          )}
        </main>
      </div>
    </>
  );
}

function PlayerFeedbackSection({ selectedClubId }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const limit = 10;

  const { data: feedbackData, isLoading } = useQuery({
    queryKey: ['playerFeedback', selectedClubId, currentPage, limit],
    queryFn: () => superAdminAPI.getPlayerFeedback(selectedClubId, currentPage, limit),
    enabled: !!selectedClubId,
    keepPreviousData: true,
  });

  const feedback = feedbackData?.feedback || [];
  const pagination = feedbackData?.pagination || { total: 0, page: 1, totalPages: 1 };

  const sortedFeedback = [...feedback].sort((a, b) => {
    const dir = sortOrder === 'asc' ? 1 : -1;
    if (sortBy === 'date') {
      return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }
    if (sortBy === 'player') {
      return dir * (a.player?.name || '').localeCompare(b.player?.name || '');
    }
    return 0;
  });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="text-white space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Player Feedback</h1>
        <span className="text-sm text-gray-400">
          {pagination.total} total feedback{pagination.total !== 1 ? 's' : ''}
        </span>
      </div>

      {isLoading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading feedback...</p>
        </div>
      ) : feedback.length === 0 ? (
        <div className="bg-slate-800 rounded-xl p-16 border border-slate-700 text-center">
          <div className="text-6xl mb-4">💬</div>
          <p className="text-xl text-gray-300">No feedback submitted yet</p>
          <p className="text-gray-500 mt-2">Player feedback will appear here once submitted.</p>
        </div>
      ) : (
        <>
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                    <button onClick={() => toggleSort('player')} className="flex items-center gap-1 hover:text-white transition-colors">
                      Player Name
                      {sortBy === 'player' && (
                        <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Feedback</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                    <button onClick={() => toggleSort('date')} className="flex items-center gap-1 hover:text-white transition-colors">
                      Date
                      {sortBy === 'date' && (
                        <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {sortedFeedback.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-medium text-white">{item.player?.name || 'Unknown'}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {item.player?.email || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300 max-w-md">
                      <p className="line-clamp-3">{item.message}</p>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {item.rating ? (
                        <span className="text-yellow-400">{'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}</span>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400 whitespace-nowrap">
                      {new Date(item.createdAt).toLocaleDateString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                      <br />
                      <span className="text-xs text-gray-500">
                        {new Date(item.createdAt).toLocaleTimeString('en-IN', {
                          timeZone: 'Asia/Kolkata',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between bg-slate-800 rounded-xl p-4 border border-slate-700">
              <span className="text-sm text-gray-400">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let page;
                  if (pagination.totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= pagination.totalPages - 2) {
                    page = pagination.totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-gradient-to-r from-red-400 to-purple-600 text-white'
                          : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage >= pagination.totalPages}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
